import { useEffect, useState } from "react";

/* ─────────────────────────────────────────────
   IMAGE IMPORTS — src/assets/images/blogs/
───────────────────────────────────────────── */
import HeroImg             from "../../assets/images/blogs/battery-swapping.webp";
import HeroImgMobile       from "../../assets/images/blogs/battery-swapping-mobile.webp";
import NewsletterBgDesktop from "../../assets/images/blogs/newsletter-bg.webp";
import NewsletterMicMobile from "../../assets/images/blogs/newsletter-bg-mobile.webp";
import InlineImg           from "../../assets/images/blogs/battery-swapping-racenergy.webp";

/* ─────────────────────────────────────────────
   IsBatterySwappingAViableSolutionForIndia.jsx
   Prefix      : bd-
   Fonts       : Montserrat (headings) | Roboto (body)
   Purple      : #743FAC
   BG main     : #1A1A1A
   BG dark     : #000000
   Inner pad   : 102px desktop | 32px tab | 16px mobile
   Breaks      : 768px | 480px
   Anim        : IntersectionObserver fade-up
                 delays 0.08s / 0.18s / 0.28s / 0.38s

   EXTRAS vs Tesla page:
   - bd-quote      : italic opening quote line
   - bd-inline-img : left-aligned content image (~350px)
   - bd-subhead--bold : bold question subhead (longer text)
   - bd-link       : inline colored link #E8472A
─────────────────────────────────────────────── */

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800;900&family=Roboto:wght@300;400;500;700&display=swap');

  .bd-wrap *, .bd-wrap *::before, .bd-wrap *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
  .bd-wrap {
    font-family: 'Roboto', sans-serif;
    background: #1A1A1A;
    color: #ffffff;
    width: 100%;
    overflow-x: hidden;
  }
  .bd-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding-left: 102px;
    padding-right: 102px;
    width: 100%;
  }
  .bd-content-col {
    width: 100%;
  }

  /* ── SCROLL FADE-UP ── */
  .bd-fade-up {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .bd-fade-up.bd-in { opacity: 1; transform: translateY(0); }
  .bd-delay-1 { transition-delay: 0.08s; }
  .bd-delay-2 { transition-delay: 0.18s; }
  .bd-delay-3 { transition-delay: 0.28s; }
  .bd-delay-4 { transition-delay: 0.38s; }

  /* ══════════════════════════════════════
     HERO
  ══════════════════════════════════════ */
  .bd-hero { width: 100%; background: #000000; }
  .bd-hero__title-bar {
    width: 100%;
    background: #1A1A1A;
    padding: 60px 0px;
  }
  .bd-hero__title {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    line-height: 140%;
    text-align: center;
  }
  .bd-hero__img-wrap {
    width: 100%;
    max-height: 600px;
    overflow: hidden;
  }
  .bd-hero__img-wrap img {
    width: 100%;
    height: 100%;
    max-height: 600px;
    object-fit: cover;
    object-position: center;
    display: block;
  }

  /* ══════════════════════════════════════
     BODY CONTENT
  ══════════════════════════════════════ */
  .bd-body-section {
    width: 100%;
    background: #1A1A1A;
    padding: 60px 0 100px;
  }

  /* italic opening quote — image 1 */
  .bd-quote {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    font-style: italic;
    line-height: 160%;
    color: #E2E2E2;
    margin-bottom: 24px;
  }

  .bd-para {
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 160%;
    color: #E2E2E2;
    margin-bottom: 24px;
  }

  /* standard subhead — image 2 */
  .bd-subhead {
    font-family: 'Roboto', sans-serif;
    font-size: 20px;
    font-weight: 500;
    color: #ffffff;
    line-height: 160%;
    margin-bottom: 14px;
    margin-top: 44px;
  }

  /* bold question subhead — image 3 & 4 (longer bold question text) */
  .bd-subhead--bold {
    font-family: 'Roboto', sans-serif;
    font-size: 20px;
    font-weight: 500;
    color: #ffffff;
    line-height: 160%;
    margin-bottom: 14px;
    margin-top: 44px;
  }

  /* inline colored link — RACEnergy orange */
  .bd-link {
    color: #FF1E22;
    text-decoration: none;
  }
  .bd-link:hover {
    text-decoration: underline;
  }

  /* inline content image — left aligned, ~350px wide — image 3 */
  .bd-inline-img-wrap {
    margin: 44px 0;
    width: 424px;
    max-width: 100%;
  }
  .bd-inline-img-wrap img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 8px;
  }

  /* ══════════════════════════════════════
     NEWSLETTER
  ══════════════════════════════════════ */
  .bd-newsletter {
    width: 100%;
    position: relative;
    overflow: hidden;
    min-height: 500px;
    display: flex;
    align-items: center;
  }
  .bd-newsletter__bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    
    object-position: center;
    z-index: 0;
  }
  .bd-newsletter__overlay {
    position: absolute;
    inset: 0;
    z-index: 1;
  }
  .bd-newsletter__inner {
    position: relative;
    z-index: 2;
    width: 100%;
    max-width: 1600px;
    margin: 0 auto;
    padding: 0px 102px;
  }
  .bd-newsletter__heading {
    font-family: 'Montserrat', sans-serif;
    font-size: 50px;
    font-weight: 700;
    color: #ffffff;
    line-height: 120%;
    margin-bottom: 14px;
  }
  .bd-newsletter__sub {
    font-family: 'Roboto', sans-serif;
    font-size: 15px;
    color: rgba(255,255,255,0.72);
    line-height: 160%;
    margin-bottom: 24px;
  }
  .bd-newsletter__form {
    display: flex;
    align-items: stretch;
    max-width: 552px;
    gap: 14px;
  }
  .bd-newsletter__input {
    flex: 1;
    height: 48px;
    background: #1A1A1A;
    border: 1px solid #ffffff;
    border-radius: 5px;
    padding: 15px 14px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    color: #FFFFFF;
    outline: none;
  }
  .bd-newsletter__input::placeholder { color: #FFFFFF; }
  .bd-newsletter__btn {
    height: 50px;
    padding: 15px 30px;
    background: #743FAC;
    border: none;
    border-radius: 5px;
    font-family: 'Roboto', sans-serif;
    font-size: 16px;
    font-weight: 500;
    color: #ffffff;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    transition: background 0.25s ease;
  }
  .bd-newsletter__btn:hover { background: #5e2e90; }
  .bd-newsletter__btn-arrow {
    display: inline-flex;
    align-items: center;
    transition: transform 0.2s ease;
  }
  .bd-newsletter__btn:hover .bd-newsletter__btn-arrow { transform: translateX(4px); }
  .bd-newsletter__mic-img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: cover;
  }
  .bd-newsletter__content { padding: 0; }

  /* ══════════════════════════
     RESPONSIVE 768px
  ══════════════════════════ */
  @media (max-width: 768px) {
    .bd-inner { padding-left: 32px; padding-right: 32px; }
    .bd-hero__img-wrap img { max-height: 350px; }
    .bd-hero__title-bar { padding: 24px 32px 20px; }
    .bd-hero__title { font-size: 24px; }
    .bd-body-section { padding: 40px 0 48px; }
    .bd-para { margin-bottom: 18px; font-size: 15px; }
    .bd-quote { font-size: 15px; margin-bottom: 18px; }
    .bd-subhead { font-size: 18px; margin-top: 28px; margin-bottom: 10px; }
    .bd-subhead--bold { font-size: 18px; margin-top: 28px; margin-bottom: 10px; }
    .bd-inline-img-wrap { width: 280px; margin: 28px 0; }

    .bd-newsletter {
      min-height: auto;
      align-items: stretch;
      flex-direction: column;
      background: #1A1A1A;
    }
    .bd-newsletter__mic-img { width: 100%; height: 350px; object-fit: cover; }
    .bd-newsletter__inner {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .bd-newsletter__content {
      padding: 40px 24px 48px;
      text-align: center;
    }
    .bd-newsletter__heading { font-size: 24px; }
    .bd-newsletter__sub { font-size: 14px; margin: 0 auto 24px; }
    .bd-newsletter__form {
      flex-direction: column;
      max-width: 100%;
      gap: 12px;
    }
    .bd-newsletter__input { border-radius: 5px; height: 44px; }
    .bd-newsletter__btn {
      border-radius: 5px;
      justify-content: center;
      height: 44px;
    }
  }

  /* ══════════════════════════
     RESPONSIVE 480px
  ══════════════════════════ */
  @media (max-width: 480px) {
    .bd-inner { padding-left: 16px; padding-right: 16px; }
    .bd-hero__title-bar { padding: 30px 16px; }
    .bd-body-section { padding: 30px 0 30px; }
    .bd-hero__img-wrap img { max-height: 366px; width: 100%; }
    .bd-hero__title { font-size: 30px; line-height: 120%; }
    .bd-para { font-size: 14px; margin-bottom: 14px; }
    .bd-quote { font-size: 14px; margin-bottom: 14px; }
    .bd-subhead { margin-top: 30px; margin-bottom: 14px; font-size: 16px; }
    .bd-subhead--bold { margin-top: 30px; margin-bottom: 14px; font-size: 16px; }
    .bd-inline-img-wrap { width: 100%; margin: 30px 0; }

    .bd-newsletter {
      min-height: auto;
      align-items: stretch;
      flex-direction: column;
      background: #1A1A1A;
    }
    .bd-newsletter__mic-img { width: 100%; height: 366px; object-fit: cover; }
    .bd-newsletter__inner {
      padding: 0;
      display: flex;
      flex-direction: column;
    }
    .bd-newsletter__content {
      padding: 30px 16px 40px;
      text-align: center;
    }
    .bd-newsletter__heading { font-size: 30px; line-height: 120%; }
    .bd-newsletter__sub { font-size: 14px; max-width: 275px; margin: 0 auto 24px; }
    .bd-newsletter__form {
      flex-direction: column;
      max-width: 100%;
      gap: 15px;
      margin-top: 15px;
    }
    .bd-newsletter__input { border-radius: 5px; height: 40px; font-size: 14px; }
    .bd-newsletter__btn {
      border-radius: 5px;
      justify-content: center;
      height: 40px;
      font-size: 14px;
    }
  }
`;

/* ── SCROLL REVEAL ── */
function useScrollReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".bd-fade-up");
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("bd-in"); }),
      { threshold: 0.12 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

/* ── ARROW ── */
function ArrowRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

/* ── NEWSLETTER ── */
function Newsletter({ isMobile }) {
  const [email, setEmail] = useState("");
  return (
    <section className="bd-newsletter">
      {!isMobile && (
        <>
          <img className="bd-newsletter__bg" src={NewsletterBgDesktop} alt="" aria-hidden="true" />
          <div className="bd-newsletter__overlay" />
        </>
      )}
      <div className="bd-newsletter__inner">
        {isMobile && (
          <img className="bd-newsletter__mic-img" src={NewsletterMicMobile} alt="Microphone" />
        )}
        <div className="bd-newsletter__content">
          <h2 className="bd-newsletter__heading">Sign up for our<br />newsletter!</h2>
          <p className="bd-newsletter__sub">Get notified about updates and be the first to get early access to new episodes.</p>
          <div className="bd-newsletter__form">
            <input
              className="bd-newsletter__input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button className="bd-newsletter__btn">
              Subscribe
              <span className="bd-newsletter__btn-arrow"><ArrowRight /></span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── ROOT EXPORT ── */
export default function IsBatterySwappingAViableSolutionForIndia() {
  useScrollReveal();

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const [isSmallMobile, setIsSmallMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 480px)");
    setIsSmallMobile(mq.matches);
    const handler = (e) => setIsSmallMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  return (
    <>
      <style>{CSS}</style>
      <div className="bd-wrap">

        {/* HERO */}
        <section className="bd-hero">
          <div className="bd-hero__title-bar">
            <h1 className="bd-hero__title">Is Battery Swapping a viable solution for India?</h1>
          </div>
          <div className="bd-hero__img-wrap">
            <img
              src={isSmallMobile ? HeroImgMobile : HeroImg}
              alt="Battery Swapping"
            />
          </div>
        </section>

        {/* BODY */}
        <section className="bd-body-section">
          <div className="bd-inner">
            <div className="bd-content-col">

              {/* italic opening quote — image 1 */}
              <p className="bd-quote bd-fade-up bd-delay-1">
                "Battery-as-a-service or battery swapping allows EV owners to replace their depleted batteries with freshly charged ones at swap stations."
              </p>

              <p className="bd-para bd-fade-up bd-delay-1">The automotive industry is going through a revolution. India has already established aggressive emission reduction objectives, and the vehicle sector is one critical area where emissions can be dramatically reduced. Today every debate on EV adoption will inevitably lead to three of its major drawbacks. To begin with, the initial cost of an EV is relatively expensive as compared to their ICE equivalents, followed by the usual charging infrastructure, and finally, the physical space necessary in metropolitan areas for these public chargers.</p>

              <p className="bd-para bd-fade-up bd-delay-1">As the EV adoption increases, the attention is shifting to building the infrastructure, such as charging and battery swapping stations. It's clear after the recent budget that the government aims to establish battery-swap service standards, focusing on electric two and three-wheelers that ferry customers and cater to last-mile delivery.</p>

              <p className="bd-para bd-fade-up bd-delay-1">In light of the limited space available in metropolitan areas for large-scale charging stations, Finance Minister Nirmal Sitharaman declared that the government will implement a battery swapping policy as well as interoperability standards. The government will encourage commercial companies to develop sustainable and creative business models for battery swapping, often known as "Battery or Energy as a Service." "This will boost EV ecosystem efficiency" the finance minister stated during the presentation of the Union Budget for 2022-23. According to a draught note provided by the government think tank, NITI Aayog is collaborating with the Bureau of Indian Standards, the Department of Science and Technology, and others to create precise technical standards for batteries.</p>

              <p className="bd-para bd-fade-up bd-delay-1">To fully comprehend India's consideration of battery swapping, we must first grasp the notion of battery swapping or battery-as-a-service.</p>

              {/* subhead — image 2 */}
              <h2 className="bd-subhead bd-fade-up bd-delay-1">What does Battery Swapping mean?</h2>

              <p className="bd-para bd-fade-up bd-delay-1">Battery swapping, as the name implies, is a means of keeping a vehicle operating by switching out the battery. Hence, battery swapping, also known as battery-as-a-service, enables EV owners to change out exhausted batteries for charged ones at swap stations. This will solve the issue of waiting time at public charging stations, as well as drivers' range concerns. Furthermore, battery leasing can assist EV owners to save money on battery purchases. In comparison to charging at a charging station, which might take hours, in theory, the battery swapping service is faster and takes only a few minutes. This technology has opened a world of possibilities for fleet owners who want to keep their cars operating without worrying about charging time.</p>

              <h2 className="bd-subhead bd-fade-up bd-delay-1">What are the pros and cons of this technology?</h2>

              <p className="bd-para bd-fade-up bd-delay-1">The perks of a battery swapping system are obvious, such as the ability to charge batteries in minutes, allowing for more flexibility in a swap facility. With regulated charging conditions, battery leasing replaces battery ownership, lowering the initial cost of EVs and extending battery life projection, and not forgetting battery swap station takes up a fraction of the area of a charging station.</p>

              <p className="bd-para bd-fade-up bd-delay-1">On the other hand, the detriments need more reflection. For instance, there is a lack of consistency across electric vehicle batteries, and the battery pack design is not suitable for quick repair. Battery packs also have a shorter commercial life owing to customer demand for new batteries with a longer range and power for several EVs. Following that, OEMs' slow adoption of charging methods, higher battery leasing prices throughout the EV's life, and a higher GST on separate batteries (18%) compared to batteries sold with the EV (5%) are roadblocks that have to be tackled.</p>

              <p className="bd-para bd-fade-up bd-delay-1">Conversely, as the demand for EVs grows, manufacturers are steadily lining up and testing newer models. Many commercial companies and government-backed organizations, such as EESL, BSES, and Sun Mobility, are already launching charging stations in the battery swapping area. Fleet operators such as Delhivery, Flipkart, Amazon, and Dominoes, on the other hand, have expressed interest in using the Swapping Battery advantages to cut delivery costs and preserve the environment. Similarly, Ola has its e-rickshaw battery changing plant in Gurgaon where around 14 battery-swapping devices, each with 20 battery packs, powering 100+ e-rickshaws at the station.</p>

              <p className="bd-para bd-fade-up bd-delay-1">However, there are a few concerns that require quick attention when it comes to delivering Battery-as-a-service.</p>

              {/* inline image — image 3, left aligned ~350px */}
              <div className="bd-inline-img-wrap bd-fade-up bd-delay-2">
                <img src={InlineImg} alt="Arun Sreyas Reddy - RACEnergy" />
              </div>

              <p className="bd-para bd-fade-up bd-delay-1">
                We spoke with Arun Sreyas Reddy, the founder of <a href="https://racenergy.in" target="_blank" rel="noopener noreferrer" className="bd-link">RACEnergy</a>, one of the most promising players in the industry which supplies fully swappable battery packs and battery swapping stations for EVs, about this. Their aim is to set up the most extensive battery-swapping network in India and establish their presence in other continents by 2023.
              </p>

              {/* bold question subheads — image 3 & 4 */}
              <h2 className="bd-subhead--bold bd-fade-up bd-delay-1">What kind of squabbles would battery swapping cause in India? How successfully will the battery swap concept be received by the market?</h2>

              <p className="bd-para bd-fade-up bd-delay-1">Batteries and swapping technologies must be excellent for a battery swapping model to be successful. Unfortunately, several hastily made items on the market are not suitable for swapping. When done well, swapping has enormous market potential, particularly in a place like India. Swapping simulates the petroleum feel without the need to acquire batteries or worry about battery warranties. Swapping is a boon for customers who lack parking space and a sufficient electrical connection. It is especially useful when users must be on the road as much as possible and do not have time to charge their automobiles.</p>

              <h2 className="bd-subhead--bold bd-fade-up bd-delay-1">Battery swapping may be required by fleet operators to save money. However, alternative technologies such as rapid charging or fuel cells may outperform swappable batteries, resulting in swappable batteries being obsolete.</h2>

              <p className="bd-para bd-fade-up bd-delay-1">While fleet operators employ battery swapping, they are not the best users. The majority of fleet operators have the funds, parking space, and power to acquire vehicles on a charging basis. Most of them even have set itineraries and can schedule their journeys and charge appropriately. The above-mentioned users will benefit from battery charging. Flow batteries or fuel cells are useful for big vehicles such as buses or lorries that move between cities. Flow batteries are also great for storing energy. These would be tough to utilize in tiny vehicles such as two-wheelers and three-wheelers. One type of fuel or cell will not dominate transportation in the future. Instead, all sources of energy and cells, such as petrol and diesel, will coexist.</p>

              <h2 className="bd-subhead--bold bd-fade-up bd-delay-1">When it comes to electric 2-wheelers or even 3-wheelers, battery swapping is simple, however when it comes to e-buses and e-trucks, which have massive batteries, how automated will battery swapping stations have to be? Furthermore, it would take up more room than charging stations?</h2>

              <p className="bd-para bd-fade-up bd-delay-1">These stations must be fully automated with robotic arms. The robotic arm would swap the vehicle's and the battery bay's batteries. This procedure usually takes around 5 minutes. No, this does not take up any more room than a charging station. A typical charging station with two chargers (assuming a super-fast charging duration of 30 minutes, for which there are additional infra and grid-related issues) would require parking space for two cars and could only address those two vehicles in those 30 minutes. An automobile exchange station, on the other hand, would require two parking spots. One parking spot for car swapping and the other for battery storage. There is no limit to the number of batteries that may be kept here; they can be stacked quite high because the robotic arm will pick them up, similar to bicycle parking machines in Japan. This spot can address 6 automobiles at the same time with a switch time of 5 minutes.</p>

              <h2 className="bd-subhead--bold bd-fade-up bd-delay-1">Vehicles and batteries are not standardized by automakers, and at the moment it seems that they have no plans to do so. How can battery swap solve this problem without batteries that can plug and play with any vehicle, SUV, or pickup?</h2>

              <p className="bd-para bd-fade-up bd-delay-1">Standardization is not intended for vehicles or batteries. It will not be conceivable, and achieving such a feat would be science fiction. There are several types of vehicles, each with its own set of applications. Because one form factor cannot fit all, swapping is not intended to be standardized. Furthermore, various types of batteries of different brands and chemistries cannot coexist in a car (since you quite often require more than 1 battery). What would happen is that there would be more than one battery form factor – most likely three to four standards – and these standards would coexist.</p>

              <p className="bd-para bd-fade-up bd-delay-1">On the other hand, there are a few things that the government and industry have been working on to improve the quality of support it provides to end-users to provide Battery-as-a-service.</p>

              <p className="bd-para bd-fade-up bd-delay-1">Battery Swapping is a high-CAPEX industry, but its viability is improving with each passing day. Furthermore, with the notification of the PLI ACC plan, we are on the verge of cell-level localization in India, which will result in cheaper batteries in 3 to 5 years. Gogoro, a small town in Taiwan, has already shown the rest of the world that battery swapping for light vehicles can be a big success. The important element to remember is that EO must maintain a high density of swapping outlets across the city. While some companies have chosen to use existing petrol pumps, others have chosen to build up switching stations in Kirana stores, where shop owners stock 2/3 batteries that can be switched quickly. Finally, India's EV objectives are predicated on the successful conversion of 2W and 3W automobiles into electric vehicles. The swapping game is only getting started, and it has enormous potential in a nation like India, which leads the world in light vehicle form factors.</p>

            </div>
          </div>
        </section>

        {/* NEWSLETTER */}
        <Newsletter isMobile={isMobile} />

      </div>
    </>
  );
}